import { Fragment, useState } from "react";
import GenericTable from "@/components/GenericTable";
import TableFooter from "@/components/TableFooter";
import { Tooltip } from "@/components/ui/tooltip";
import { statusMessagesMapping } from "@/constants/paymentStatusMessages";
import useQueryParams from "@/hooks/useQueryParams";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import {
  Badge,
  Box,
  HStack,
  IconButton,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronDown, ChevronRight, Download, MailCheck } from "lucide-react";
import { useBuildingManagementEnabled } from "../../hooks/useBuildingManagementEnabled";
import { useShowGmf } from "../../hooks/useShowGmf";
import { useDownloadPayoutReceipt } from "../../hooks/useDownloadPayoutReceipt";
import { useFetchPayouts } from "../../hooks/useFetchPayouts";
import { getPayoutChannelName } from "../../mappers/banks.mapper";
import type { Payout, PayoutsFiltersType } from "../../types/payouts.type";
import BatchItemsFilters from "./BatchItemsFilters";

const TABLE_KEY = "payouts-batch-items";

export default function BatchItemsTable({ batchId }: { batchId: string }) {
  const { payoutId } = useQueryParams(["payoutId"]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const isBuildingManagementEnabled = useBuildingManagementEnabled();
  const showGmf = useShowGmf();

  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<PayoutsFiltersType>(TABLE_KEY, () =>
      payoutId
        ? ({ id: payoutId } as PayoutsFiltersType)
        : ({} as PayoutsFiltersType)
    );

  const { items, nextCursor, isLoading } = useFetchPayouts(
    batchId,
    queryFilters,
    cursor
  );

  const { downloadReceipt, downloadingIds } = useDownloadPayoutReceipt();

  const columns = [
    {
      key: "customerName",
      label: "Titular",
      exportTransform: (payout: Payout) => payout.customerName,
    },
    {
      key: "customerDocumentNumber",
      label: "No. Documento",
      exportTransform: (payout: Payout) => payout.customerDocumentNumber,
    },
    {
      key: "id",
      label: "ID",
      exportTransform: (payout: Payout) => payout.id,
    },
    {
      key: "accountNumber",
      label: "No. Cuenta",
      exportTransform: (payout: Payout) => payout.accountNumber ?? "",
    },
    {
      key: "bank",
      label: "Banco",
      exportTransform: (payout: Payout) => getPayoutChannelName(payout),
    },
    {
      key: "status",
      label: "Estado",
      exportTransform: (payout: Payout) => {
        const statusMap: Record<string, string> = {
          completed: "Completado",
          pending: "Pendiente",
          processing: "En proceso",
          cancelled: "Cancelado",
          declined: "Rechazado",
        };
        return statusMap[payout.status] || payout.status;
      },
    },
    {
      key: "amount",
      label: "Monto",
      exportTransform: (payout: Payout) => payout.amount,
    },
    ...(isBuildingManagementEnabled
      ? [
          {
            key: "payoutItems",
            label: "Inmuebles",
            exportTransform: (payout: Payout) =>
              payout.payoutItems?.length ?? 0,
          },
        ]
      : []),
    ...(showGmf
      ? [
          {
            key: "gmf",
            label: "GMF",
            exportTransform: (payout: Payout) => payout.gmf ?? 0,
          },
        ]
      : []),
    { key: "actions", label: "" },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Bogota",
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge colorPalette="green" variant="subtle">
            Completado
          </Badge>
        );
      case "pending":
        return (
          <Badge colorPalette="orange" variant="subtle">
            Pendiente
          </Badge>
        );
      case "processing":
        return (
          <Badge colorPalette="blue" variant="subtle">
            En proceso
          </Badge>
        );
      case "cancelled":
        return (
          <Badge colorPalette="red" variant="subtle">
            Cancelado
          </Badge>
        );
      case "declined":
        return (
          <Badge colorPalette="red" variant="subtle">
            Rechazado
          </Badge>
        );
      default:
        return (
          <Badge colorPalette="gray" variant="subtle">
            {status}
          </Badge>
        );
    }
  };

  return (
    <Box mb={8}>
      <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.800">
        Detalle de Pagos
      </Text>
      <BatchItemsFilters
        queryFilters={queryFilters}
        setQueryFilters={setQueryFilters}
      />
      <GenericTable isLoading={isLoading} columns={columns}>
        {items.map((item) => {
          const payoutItems = item.payoutItems ?? [];
          const isExpanded = expandedId === item.id;

          return (
            <Fragment key={item.id}>
              <Table.Row
                bg="white"
                cursor={payoutItems.length ? "pointer" : undefined}
                _hover={payoutItems.length ? { bg: "gray.50" } : undefined}
                onClick={
                  payoutItems.length
                    ? () => setExpandedId(isExpanded ? null : item.id)
                    : undefined
                }
              >
                <Table.Cell>
                  <HStack gap={2}>
                    {!!payoutItems.length &&
                      (isExpanded ? (
                        <ChevronDown size={14} color="#71717a" />
                      ) : (
                        <ChevronRight size={14} color="#71717a" />
                      ))}
                    <Text fontWeight="medium" color="gray.800">
                      {item.customerName}
                    </Text>
                  </HStack>
                </Table.Cell>
                <Table.Cell color="gray.600">
                  {item.customerDocumentNumber}
                </Table.Cell>
                <Table.Cell color="gray.600">{item.id}</Table.Cell>
                <Table.Cell color="gray.600">{item.accountNumber}</Table.Cell>
                <Table.Cell color="gray.600">
                  {getPayoutChannelName(item)}
                </Table.Cell>
                <Table.Cell>
                  {item.statusMessage ? (
                    <Tooltip
                      content={
                        <Box p={2}>
                          <Text fontSize="sm" fontWeight="medium">
                            {statusMessagesMapping[item.statusMessage]}
                          </Text>
                        </Box>
                      }
                    >
                      {getStatusBadge(item.status)}
                    </Tooltip>
                  ) : (
                    getStatusBadge(item.status)
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Text fontWeight="bold" color="gray.800">
                    {formatCurrency(item.amount)}
                  </Text>
                </Table.Cell>
                {isBuildingManagementEnabled && (
                  <>
                    <Table.Cell color="gray.600">
                      {payoutItems.length}
                    </Table.Cell>
                  </>
                )}
                {showGmf && (
                  <Table.Cell>
                    <Text fontWeight="medium" color="gray.600">
                      {formatCurrency(item.gmf ?? 0)}
                    </Text>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <HStack gap={2}>
                    <IconButton
                      aria-label="Descargar comprobante"
                      variant="ghost"
                      size="xs"
                      onClick={(event) => {
                        event.stopPropagation();
                        downloadReceipt(item.id, !!item.receiptGenerated);
                      }}
                      disabled={downloadingIds.has(item.id)}
                    >
                      {downloadingIds.has(item.id) ? (
                        <Spinner size="sm" />
                      ) : (
                        <Download size="1.2rem" />
                      )}
                    </IconButton>
                    {item.ownerNotifiedAt && item.customerEmail && (
                      <Tooltip
                        content={
                          <Box p={2}>
                            <Text fontSize="sm" fontWeight="medium">
                              Notificación enviada
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {formatDate(item.ownerNotifiedAt)}
                            </Text>
                            <Text fontSize="xs" mt={1}>
                              {item.customerEmail}
                            </Text>
                          </Box>
                        }
                      >
                        <Box color="green.500">
                          <MailCheck size={16} />
                        </Box>
                      </Tooltip>
                    )}
                  </HStack>
                </Table.Cell>
              </Table.Row>
              {isExpanded && (
                <Table.Row bg="white">
                  <Table.Cell colSpan={columns.length} px={10} py={0}>
                    <VStack gap={0} align="stretch" py={1}>
                      {payoutItems.map((payoutItem, index) => (
                        <HStack
                          key={`${payoutItem.description}-${index}`}
                          justify="space-between"
                          py={2.5}
                          borderBottomWidth="1px"
                          borderColor="#ebecef80"
                          _last={{ borderBottomWidth: 0 }}
                        >
                          <Text fontSize="sm" color="gray.600">
                            {payoutItem.description || "-"}
                          </Text>
                          <Text fontSize="sm" color="gray.800">
                            {formatCurrency(payoutItem.amount)}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Table.Cell>
                </Table.Row>
              )}
            </Fragment>
          );
        })}
      </GenericTable>
      <TableFooter
        tableProps={{
          fileName: "pagos",
          items,
          columns,
          pagination: {
            tableKey: TABLE_KEY,
            nextCursor,
            setCursor,
            cursor,
          },
        }}
      />
    </Box>
  );
}
